import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, ArrowRight, Check, Link2, Pencil, Plus, RotateCcw, Trash2, X, Zap,
} from 'lucide-react';
import { ApiClientError, DataTable, EmptyState, Spinner, relativeTime, type Column } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Payment routes
 *
 *  A route is one upstream endpoint a payment can be handed to. This screen is
 *  the operator's whole relationship with them: which exist, which are on,
 *  which are struggling, and what the last few calls actually returned.
 *
 *  The enable switch is the control that matters most, so it is the one thing
 *  on each row you can reach without opening anything — turning a bad provider
 *  off at 2am should not require finding a form first.
 * ========================================================================== */

type Kind = 'UPI_INTENT' | 'CHECKOUT';

interface Route {
  id: string;
  name: string;
  code: string;
  kind: Kind;
  mode: 'TEST' | 'LIVE';
  is_enabled: boolean;
  priority: number;
  description: string | null;
  endpoint_url: string;
  http_method: 'POST' | 'GET';
  content_type: 'JSON' | 'FORM';
  auth_scheme: string;
  auth_header_name: string | null;
  has_credential: boolean;
  has_signing_secret: boolean;
  timeout_ms: number;
  max_concurrent: number;
  min_amount: string;
  max_amount: string;
  daily_cap: string | null;
  health: 'HEALTHY' | 'DEGRADED' | 'DOWN';
  inflight_count: number;
  consecutive_failures: number;
  success_count: number;
  failure_count: number;
  volume_today: string;
  last_used_at: string | null;
  last_latency_ms: number | null;
  last_error: string | null;
  callback_url: string;
}

interface Attempt {
  id: string;
  route: { name: string; code: string };
  kind: Kind;
  outcome: string;
  http_status: number | null;
  latency_ms: number | null;
  error_message: string | null;
  payment_id: string | null;
  payment_status: string | null;
  merchant: { ref: string; name: string } | null;
  created_at: string;
}

const rupees = (amount: string | number) =>
  `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function HealthDot({ health, enabled }: { health: Route['health']; enabled: boolean }) {
  const color = !enabled
    ? 'bg-content-faint'
    : health === 'HEALTHY'
      ? 'bg-brand-500'
      : health === 'DEGRADED'
        ? 'bg-warning'
        : 'bg-danger';
  const label = !enabled ? 'Disabled' : health.toLowerCase();
  return (
    <span className="flex items-center gap-2">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${color}`} aria-hidden="true" />
      <span className="text-2xs uppercase tracking-wider text-content-faint">{label}</span>
    </span>
  );
}

export function Routes() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Route | 'new' | null>(null);
  const [probeResult, setProbeResult] = useState<{ route: string; ok: boolean; body: unknown } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attemptFilter, setAttemptFilter] = useState<string | null>(null);

  const routes = useQuery({
    queryKey: ['routes'],
    queryFn: () => api.get<{ data: Route[] }>('/routes'),
    refetchInterval: 15_000,
  });

  const attempts = useQuery({
    queryKey: ['route-attempts', attemptFilter],
    queryFn: () => api.get<{ data: Attempt[] }>(`/route-attempts${attemptFilter ? `?route_id=${attemptFilter}` : ''}`),
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['routes'] });
    void queryClient.invalidateQueries({ queryKey: ['route-attempts'] });
  };
  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof ApiClientError ? err.message : fallback);

  const toggle = useMutation({
    mutationFn: (v: { id: string; is_enabled: boolean }) => api.post(`/routes/${v.id}/enabled`, { is_enabled: v.is_enabled }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => fail(err, 'Could not change that route.'),
  });

  const resetHealth = useMutation({
    mutationFn: (id: string) => api.post(`/routes/${id}/reset-health`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => fail(err, 'Could not reset health.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/routes/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => fail(err, 'Could not delete that route.'),
  });

  const probe = useMutation({
    mutationFn: (route: Route) =>
      api.post<{ ok: boolean; http_status: number | null; latency_ms: number; message: string | null; response: unknown }>(
        `/routes/${route.id}/probe`,
        { amount: 1 },
      ).then((r) => ({ route: route.name, ok: r.ok, body: r })),
    onSuccess: (result) => {
      setError(null);
      setProbeResult(result);
    },
    onError: (err) => fail(err, 'Probe failed to run.'),
  });

  const all = routes.data?.data ?? [];
  const groups: { kind: Kind; title: string; blurb: string }[] = [
    {
      kind: 'UPI_INTENT',
      title: 'UPI intent routes',
      blurb: 'Return a upi://pay deep link. Used by the intent API and by checkout when a route offers one.',
    },
    {
      kind: 'CHECKOUT',
      title: 'Checkout routes',
      blurb: 'Return a hosted payment page. Used by the hosted checkout link.',
    },
  ];

  return (
    <div className="space-y-10">
      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {probeResult && (
        <div className={`rounded-lg border p-4 ${probeResult.ok ? 'border-brand-800 bg-brand-soft' : 'border-danger-ring bg-danger-soft'}`}>
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm font-medium text-content">
              Probe · {probeResult.route} · {probeResult.ok ? 'route answered correctly' : 'route did not answer usably'}
            </p>
            <button type="button" onClick={() => setProbeResult(null)} className="btn btn-ghost h-6 w-6 p-0" aria-label="Dismiss">
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
          <pre className="numeral mt-3 max-h-60 overflow-auto rounded border border-line bg-surface-inset p-3 text-2xs leading-relaxed text-content-muted">
            {JSON.stringify(probeResult.body, null, 2)}
          </pre>
        </div>
      )}

      {editing && (
        <RouteForm
          route={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setError(null);
            invalidate();
          }}
        />
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Payment routes</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
            Each payment is handed to the eligible route that is least busy, and among equals the one used longest ago.
            Turning a route off takes it out of rotation immediately; payments already on it are unaffected.
          </p>
        </div>
        {!editing && (
          <button type="button" onClick={() => { setEditing('new'); setError(null); }} className="btn btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add route
          </button>
        )}
      </div>

      {routes.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : routes.isError ? (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Could not load routes.{' '}
          {routes.error instanceof ApiClientError ? routes.error.message : 'The request failed.'}
        </p>
      ) : (
        groups.map((group) => {
          const rows = all.filter((r) => r.kind === group.kind);
          return (
            <section key={group.kind}>
              <div className="mb-3 border-b border-line pb-2.5">
                <h3 className="text-md font-medium tracking-tight">{group.title}</h3>
                <p className="mt-1 text-sm text-content-subtle">{group.blurb}</p>
              </div>

              {rows.length === 0 ? (
                <div className="panel">
                  <EmptyState
                    title="No routes of this kind"
                    description="Until one exists and is enabled, requests of this kind are refused rather than routed somewhere unintended."
                  />
                </div>
              ) : (
                <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
                  {rows.map((route) => (
                    <li key={route.id} className="px-4 py-4 sm:px-5">
                      <div className="flex flex-wrap items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2.5">
                            <p className="text-sm font-medium text-content">{route.name}</p>
                            <span className={`badge ${route.mode === 'LIVE' ? 'badge-success' : 'badge-neutral'}`}>
                              {route.mode.toLowerCase()}
                            </span>
                            <HealthDot health={route.health} enabled={route.is_enabled} />
                          </div>

                          <p className="numeral mt-1.5 truncate text-xs text-content-subtle">
                            {route.http_method} {route.endpoint_url}
                          </p>
                          {route.description && (
                            <p className="mt-1 text-xs leading-relaxed text-content-faint">{route.description}</p>
                          )}

                          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-2xs">
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">In flight</dt>
                              <dd className="numeral text-content-muted">
                                {route.inflight_count}
                                {route.max_concurrent > 0 ? ` / ${route.max_concurrent}` : ' / ∞'}
                              </dd>
                            </div>
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">Succeeded</dt>
                              <dd className="numeral text-content-muted">{route.success_count}</dd>
                            </div>
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">Failed</dt>
                              <dd className="numeral text-content-muted">{route.failure_count}</dd>
                            </div>
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">Today</dt>
                              <dd className="numeral text-content-muted">{rupees(route.volume_today)}</dd>
                            </div>
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">Band</dt>
                              <dd className="numeral text-content-muted">
                                {rupees(route.min_amount)}–{rupees(route.max_amount)}
                              </dd>
                            </div>
                            <div className="flex gap-1.5">
                              <dt className="text-content-faint">Last used</dt>
                              <dd className="text-content-muted">{route.last_used_at ? relativeTime(route.last_used_at) : 'never'}</dd>
                            </div>
                            {route.last_latency_ms !== null && (
                              <div className="flex gap-1.5">
                                <dt className="text-content-faint">Latency</dt>
                                <dd className="numeral text-content-muted">{route.last_latency_ms}ms</dd>
                              </div>
                            )}
                          </dl>

                          {route.last_error && (
                            <p className="mt-2.5 flex items-start gap-1.5 text-2xs leading-relaxed text-[#FCA5A5]">
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                              {route.last_error}
                            </p>
                          )}

                          <p className="numeral mt-2.5 flex items-center gap-1.5 text-2xs text-content-faint">
                            <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
                            {route.callback_url}
                          </p>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <button
                            type="button"
                            onClick={() => toggle.mutate({ id: route.id, is_enabled: !route.is_enabled })}
                            disabled={toggle.isPending}
                            aria-pressed={route.is_enabled}
                            className={`flex h-6 w-11 items-center rounded-full border px-0.5 transition-colors ${
                              route.is_enabled ? 'border-brand-700 bg-brand-500/90' : 'border-line bg-surface-inset'
                            }`}
                            aria-label={route.is_enabled ? `Disable ${route.name}` : `Enable ${route.name}`}
                          >
                            <span
                              className={`h-4 w-4 rounded-full bg-white transition-transform ${
                                route.is_enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>

                          <div className="flex flex-wrap justify-end gap-1.5">
                            <button type="button" onClick={() => probe.mutate(route)} disabled={probe.isPending} className="btn btn-ghost btn-sm px-1.5">
                              <Zap className="h-3 w-3" aria-hidden="true" />
                              Probe
                            </button>
                            <button type="button" onClick={() => setEditing(route)} className="btn btn-ghost btn-sm px-1.5">
                              <Pencil className="h-3 w-3" aria-hidden="true" />
                              Edit
                            </button>
                            <button type="button" onClick={() => setAttemptFilter(route.id)} className="btn btn-ghost btn-sm px-1.5">
                              <Activity className="h-3 w-3" aria-hidden="true" />
                              Log
                            </button>
                            {route.health !== 'HEALTHY' && (
                              <button type="button" onClick={() => resetHealth.mutate(route.id)} className="btn btn-ghost btn-sm px-1.5">
                                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                                Reset
                              </button>
                            )}
                            {route.success_count === 0 && route.failure_count === 0 && (
                              <button
                                type="button"
                                onClick={() => remove.mutate(route.id)}
                                className="btn btn-ghost btn-sm px-1.5 text-content-subtle hover:text-danger"
                              >
                                <Trash2 className="h-3 w-3" aria-hidden="true" />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}

      {/* ---- Attempt log -------------------------------------------------- */}
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-2.5">
          <div>
            <h3 className="text-md font-medium tracking-tight">Upstream calls</h3>
            <p className="mt-1 text-sm text-content-subtle">
              Every request we made and what came back. This is the answer to “the payment page never loaded”.
            </p>
          </div>
          {attemptFilter && (
            <button type="button" onClick={() => setAttemptFilter(null)} className="btn btn-secondary btn-sm">
              <X className="h-3 w-3" aria-hidden="true" />
              Clear route filter
            </button>
          )}
        </div>

        <DataTable
          columns={
            [
              {
                key: 'route',
                header: 'Route',
                render: (a: Attempt) => (
                  <div className="min-w-0">
                    <p className="text-xs text-content">{a.route.name}</p>
                    <p className="numeral mt-0.5 text-2xs text-content-faint">{a.route.code}</p>
                  </div>
                ),
              },
              {
                key: 'outcome',
                header: 'Outcome',
                render: (a: Attempt) => (
                  <span className={`badge ${a.outcome === 'SUCCESS' ? 'badge-success' : 'badge-danger'}`}>
                    {a.outcome.toLowerCase().replace(/_/g, ' ')}
                  </span>
                ),
              },
              {
                key: 'payment',
                header: 'Payment',
                render: (a: Attempt) => (
                  <span className="numeral text-xs text-content-subtle">{a.payment_id ?? '—'}</span>
                ),
              },
              {
                key: 'merchant',
                header: 'Merchant',
                render: (a: Attempt) => (
                  <span className="text-xs text-content-subtle">{a.merchant?.name ?? '—'}</span>
                ),
              },
              {
                key: 'http',
                header: 'HTTP',
                align: 'right',
                render: (a: Attempt) => <span className="numeral text-xs text-content-muted">{a.http_status ?? '—'}</span>,
              },
              {
                key: 'latency',
                header: 'Latency',
                align: 'right',
                render: (a: Attempt) => (
                  <span className="numeral text-xs text-content-subtle">{a.latency_ms === null ? '—' : `${a.latency_ms}ms`}</span>
                ),
              },
              {
                key: 'when',
                header: 'When',
                align: 'right',
                render: (a: Attempt) => <span className="text-xs text-content-subtle">{relativeTime(a.created_at)}</span>,
              },
            ] as Column<Attempt>[]
          }
          rows={attempts.data?.data ?? []}
          rowKey={(a) => a.id}
          loading={attempts.isLoading}
          empty={<EmptyState title="No upstream calls recorded" description="Take a payment, or run a probe on a route." />}
        />
      </section>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Create / edit
 * ------------------------------------------------------------------------ */

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="label">{label}</span>
      {children}
      {hint && <p className="mt-1.5 text-2xs leading-relaxed text-content-faint">{hint}</p>}
    </div>
  );
}

function RouteForm({
  route,
  onClose,
  onSaved,
}: {
  route: Route | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: route?.name ?? '',
    code: route?.code ?? '',
    kind: route?.kind ?? ('UPI_INTENT' as Kind),
    mode: route?.mode ?? ('TEST' as 'TEST' | 'LIVE'),
    description: route?.description ?? '',
    endpoint_url: route?.endpoint_url ?? '',
    http_method: route?.http_method ?? ('POST' as 'POST' | 'GET'),
    content_type: route?.content_type ?? ('JSON' as 'JSON' | 'FORM'),
    auth_scheme: route?.auth_scheme ?? 'NONE',
    auth_header_name: route?.auth_header_name ?? '',
    credential: '',
    signing_secret: '',
    timeout_ms: String(route?.timeout_ms ?? 15000),
    max_concurrent: String(route?.max_concurrent ?? 5),
    min_amount: String(route?.min_amount ?? '1.00'),
    max_amount: String(route?.max_amount ?? '200000.00'),
    priority: String(route?.priority ?? 100),
    is_enabled: route?.is_enabled ?? false,
  });
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        mode: form.mode,
        description: form.description.trim() || undefined,
        endpoint_url: form.endpoint_url.trim(),
        http_method: form.http_method,
        content_type: form.content_type,
        auth_scheme: form.auth_scheme,
        auth_header_name: form.auth_header_name.trim() || undefined,
        timeout_ms: Number(form.timeout_ms),
        max_concurrent: Number(form.max_concurrent),
        min_amount: form.min_amount,
        max_amount: form.max_amount,
        priority: Number(form.priority),
        is_enabled: form.is_enabled,
      };
      // Empty means "leave it alone" on edit and "no credential" on create;
      // clearing one is done by saving a single space, which the API trims.
      if (form.credential) payload.credential = form.credential;
      if (form.signing_secret) payload.signing_secret = form.signing_secret;

      if (route) return api.patch(`/routes/${route.id}`, payload);
      return api.post('/routes', { ...payload, code: form.code.trim(), kind: form.kind });
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not save the route.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
      className="rounded-lg border border-line bg-surface-raised p-5"
    >
      <div className="mb-5 flex items-start justify-between gap-4 border-b border-line pb-4">
        <div>
          <h3 className="text-sm font-medium text-content">{route ? `Edit ${route.name}` : 'New payment route'}</h3>
          <p className="mt-1 max-w-xl text-xs leading-relaxed text-content-subtle">
            The endpoint receives our documented payload and must reply with{' '}
            {form.kind === 'CHECKOUT' ? 'a hosted payment URL' : 'a UPI intent or a payee VPA'}. Outcomes arrive
            separately on the callback URL shown on the route once it is saved.
          </p>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost h-7 w-7 p-0" aria-label="Cancel">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Row label="Name" hint="Shown here and in the audit log.">
          <input required value={form.name} onChange={(e) => set('name', e.target.value)} className="input" placeholder="Acquirer North · UPI" />
        </Row>

        <Row label="Code" hint={route ? 'Fixed once created — it appears in callback URLs.' : 'Lowercase, digits and hyphens. Used in the callback URL.'}>
          <input
            required
            disabled={Boolean(route)}
            value={form.code}
            onChange={(e) => set('code', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className="input numeral disabled:opacity-60"
            placeholder="acquirer-north-upi"
          />
        </Row>

        <Row label="Kind" hint={route ? 'Fixed once created.' : 'What this endpoint returns.'}>
          <select disabled={Boolean(route)} value={form.kind} onChange={(e) => set('kind', e.target.value as Kind)} className="input disabled:opacity-60">
            <option value="UPI_INTENT">UPI intent</option>
            <option value="CHECKOUT">Checkout page</option>
          </select>
        </Row>

        <Row label="Mode" hint="A route only ever serves payments of its own mode.">
          <select value={form.mode} onChange={(e) => set('mode', e.target.value as 'TEST' | 'LIVE')} className="input">
            <option value="TEST">Test</option>
            <option value="LIVE">Live</option>
          </select>
        </Row>

        <div className="sm:col-span-2">
          <Row label="Endpoint URL" hint="Must be https outside development, and may not point at a private address.">
            <input required value={form.endpoint_url} onChange={(e) => set('endpoint_url', e.target.value)} className="input numeral" placeholder="https://psp.example.com/api/collect" />
          </Row>
        </div>

        <div className="sm:col-span-2">
          <Row label="Description">
            <input value={form.description} onChange={(e) => set('description', e.target.value)} className="input" placeholder="Primary UPI acquirer, ₹5k cap per attempt" />
          </Row>
        </div>

        <Row label="HTTP method">
          <select value={form.http_method} onChange={(e) => set('http_method', e.target.value as 'POST' | 'GET')} className="input">
            <option value="POST">POST</option>
            <option value="GET">GET</option>
          </select>
        </Row>

        <Row label="Body format">
          <select value={form.content_type} onChange={(e) => set('content_type', e.target.value as 'JSON' | 'FORM')} className="input">
            <option value="JSON">JSON</option>
            <option value="FORM">Form encoded</option>
          </select>
        </Row>

        <Row label="Authentication">
          <select value={form.auth_scheme} onChange={(e) => set('auth_scheme', e.target.value)} className="input">
            <option value="NONE">None</option>
            <option value="BEARER">Bearer token</option>
            <option value="HEADER">Custom header</option>
            <option value="BASIC">Basic (user:pass)</option>
            <option value="BODY">In the body as api_key</option>
          </select>
        </Row>

        {form.auth_scheme === 'HEADER' && (
          <Row label="Header name">
            <input value={form.auth_header_name} onChange={(e) => set('auth_header_name', e.target.value)} className="input numeral" placeholder="X-Api-Key" />
          </Row>
        )}

        <Row
          label={route?.has_credential ? 'Credential (set — leave blank to keep)' : 'Credential'}
          hint="Encrypted at rest and never returned by the API."
        >
          <input type="password" autoComplete="new-password" value={form.credential} onChange={(e) => set('credential', e.target.value)} className="input numeral" />
        </Row>

        <Row
          label={route?.has_signing_secret ? 'Callback secret (set — leave blank to keep)' : 'Callback secret'}
          hint="When set, we sign outbound calls and require a valid signature on callbacks."
        >
          <input type="password" autoComplete="new-password" value={form.signing_secret} onChange={(e) => set('signing_secret', e.target.value)} className="input numeral" />
        </Row>

        <Row label="Timeout (ms)">
          <input type="number" min={1000} max={60000} value={form.timeout_ms} onChange={(e) => set('timeout_ms', e.target.value)} className="input numeral" />
        </Row>

        <Row label="Max concurrent" hint="Payments this route may hold at once. 0 means no limit.">
          <input type="number" min={0} max={1000} value={form.max_concurrent} onChange={(e) => set('max_concurrent', e.target.value)} className="input numeral" />
        </Row>

        <Row label="Minimum amount (₹)">
          <input type="number" min={1} step="0.01" value={form.min_amount} onChange={(e) => set('min_amount', e.target.value)} className="input numeral" />
        </Row>

        <Row label="Maximum amount (₹)">
          <input type="number" min={1} step="0.01" value={form.max_amount} onChange={(e) => set('max_amount', e.target.value)} className="input numeral" />
        </Row>

        <Row label="Priority" hint="Breaks ties only. Busy-ness and recency are considered first.">
          <input type="number" min={0} max={1000} value={form.priority} onChange={(e) => set('priority', e.target.value)} className="input numeral" />
        </Row>

        <div className="flex items-end">
          <label className="flex cursor-pointer items-start gap-2.5">
            <input type="checkbox" checked={form.is_enabled} onChange={(e) => set('is_enabled', e.target.checked)} className="mt-0.5" />
            <span>
              <span className="text-sm text-content">Enabled</span>
              <span className="mt-0.5 block text-xs text-content-subtle">Put this route into rotation immediately.</span>
            </span>
          </label>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : route ? <Check className="h-4 w-4" aria-hidden="true" /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          {route ? 'Save changes' : 'Create route'}
        </button>
      </div>
    </form>
  );
}
